import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { eventsList } from '@/mocks/events';
import { useSeo } from '@/hooks/useSeo';
import { formatDate } from '@/utils/date';
import Breadcrumb from '@/components/base/Breadcrumb';
import FavoriteButton from '@/components/base/FavoriteButton';
import ShareModal from '@/components/feature/ShareModal';
import { NEWS_TYPE_LABELS } from '@/components/base/NewsCard';

function buildNewsBody(tags: string[]) {
  return [
    {
      heading: '内容概览',
      paragraphs: [
        '本篇文章整理了该条资讯的核心要点，帮助你快速了解本次更新的关键变化与影响范围。',
        `涉及到的关键词包括：${tags.join('、')}。如果你关注相关的养成与实战表现，建议结合本站的角色攻略一起阅读。`,
      ],
    },
    {
      heading: '详细说明',
      paragraphs: [
        '根据目前公开的信息，本次内容对游戏内的多项系统产生了影响，其中部分改动会直接改变既有阵容的输出循环与操作节奏。',
        '我们在整理过程中对比了更新前后的数据表现，尽量还原最贴近实际体验的结论，供你在规划资源时参考。',
      ],
    },
    {
      heading: '玩家建议',
      paragraphs: [
        '建议优先确认自己常用队伍是否受到影响，再决定是否调整配装。若资源有限，可以先把原石与素材集中在主力角色上。',
        '后续如果有进一步的官方说明或实测数据，我们也会在第一时间同步更新本文内容。',
      ],
    },
  ];
}

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const [shareOpen, setShareOpen] = useState(false);

  const news = useMemo(() => newsList.find((item) => item.id === id), [id]);
  const event = useMemo(() => eventsList.find((item) => item.id === id), [id]);

  const title = news ? news.title : event ? event.title : '内容不存在';
  const description = news ? news.excerpt : event ? event.intro : '未找到对应内容，请返回列表页重新选择。';

  useSeo({
    title: `${title} – 原神官方游戏站`,
    description: description.slice(0, 100),
    keywords: news ? news.tags.join(',') : '原神活动,原神资讯',
    canonicalPath: `/${news ? 'news' : 'events'}/${id}`,
  });

  if (!news && !event) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到这篇内容</h1>
        <p className="mt-2 text-sm text-foreground-500">它可能已被移除，或者链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const cover = news ? news.cover : event!.cover;
  const crumbs = news
    ? [{ label: '首页', to: '/' }, { label: '资讯', to: '/news' }, { label: news.category }]
    : [{ label: '首页', to: '/' }, { label: '活动', to: '/events' }, { label: event!.title }];

  const related = news
    ? newsList.filter((item) => item.id !== news.id && item.tags.some((tag) => news.tags.includes(tag))).slice(0, 4)
    : eventsList.filter((item) => item.id !== event!.id).slice(0, 4);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div>
      <div className="relative h-[280px] w-full overflow-hidden md:h-[400px]">
        <img src={cover} alt={title} title={`${title} 原神资讯`} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/30"></div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto w-full max-w-4xl px-4 pb-8 md:px-6">
            <div className="text-background-50/70">
              <Breadcrumb items={crumbs} />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">{title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-50/75 md:text-sm">
              {news ? (
                <>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {formatDate(news.date)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {news.author}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {news.readTime}
                  </span>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-schedule-line"></i>
                    {formatDate(event!.start)} — {formatDate(event!.end)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-gift-line"></i>
                    {event!.reward}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-wrap items-center gap-3 border-b border-background-200 pb-6">
          <FavoriteButton id={`${news ? 'news' : 'event'}-${id}`} variant="full" />
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-label text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-share-line"></i>
            分享
          </button>
          <div className="ml-auto flex flex-wrap gap-2">
            {(news ? news.tags : event!.tags).map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>

        <article className="mt-8">
          <p className="rounded-lg border-l-4 border-accent-400 bg-accent-50 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {news ? news.excerpt : event!.intro}
          </p>

          {news ? (
            <div className="mt-8 space-y-8">
              {buildNewsBody(news.tags).map((section) => (
                <section key={section.heading}>
                  <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                    <a href={`#${section.heading}`}>{section.heading}</a>
                  </h2>
                  <div className="mt-3 space-y-3">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 12)} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="mt-8 space-y-8">
              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  <a href="#reward">活动奖励</a>
                </h2>
                <div className="mt-3 rounded-xl border border-background-200 bg-background-50 p-4">
                  <p className="flex items-center gap-2 text-sm text-foreground-700 md:text-base">
                    <span className="flex h-6 w-6 items-center justify-center text-accent-600">
                      <i className="ri-gift-2-line text-lg"></i>
                    </span>
                    {event!.reward}
                  </p>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  <a href="#tips">参与建议</a>
                </h2>
                <ul className="mt-3 space-y-2.5">
                  {event!.tips.map((tip, index) => (
                    <li key={tip} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-label text-primary-700">
                        {index + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </article>

        <section className="mt-14">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            <a href="#related">相关推荐</a>
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {related.map((item) => (
              <Link
                key={item.id}
                to={news ? `/news/${item.id}` : `/events/${item.id}`}
                className="gs-card-hover group flex gap-3 rounded-xl border border-background-200 bg-background-50 p-3"
              >
                <div className="h-20 w-24 shrink-0 overflow-hidden rounded-lg">
                  <img src={item.cover} alt={item.title} title={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">{item.title}</h3>
                  <p className="mt-1.5 line-clamp-2 text-xs text-foreground-500">{'excerpt' in item ? item.excerpt : item.intro}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={title} url={currentUrl} />
    </div>
  );
}